import { API, CONTENT, QUIZ, VIDEOS, EXERCISES, MIXPANEL } from '@/clients'
import { djangoErrorHandler, shortenUUIDs, isParentPresent } from '@/helpers'

const fetchJWVideo = async payload => {
  const { id, parentType, parentId } = payload
  const route = isParentPresent({ parentId, parentType })
    ? `videos/${id}/media?parent_type=${parentType}&parent_id=${parentId}`
    : `videos/${id}/media`
  return VIDEOS.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchRecentActivity = async () => {
  const route = `courses/content-activity`
  try {
    const { data } = await CONTENT.get(route)
    return data
  } catch (error) {
    djangoErrorHandler(error)
  }
}

const fetchVideos = async videoIds => {
  const route = `videos?page_size=none&ids=${shortenUUIDs(videoIds).join(',')}`
  return VIDEOS.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchVideosForBootcamp = async videoIdsAndBootcampId => {
  const { videoIds, bootcampId } = videoIdsAndBootcampId
  const route = `videos?page_size=none&ids=${shortenUUIDs(videoIds).join(
    ','
  )}&parent_type=bootcamp&parent_id=${bootcampId}`
  try {
    const { data } = await VIDEOS.get(route)
    return data
  } catch (error) {
    throw djangoErrorHandler(error)
  }
}

const fetchVideosForCourse = async videoIdsAndCourseId => {
  const { videoIds, courseId } = videoIdsAndCourseId
  const route = `videos?page_size=none&ids=${shortenUUIDs(videoIds).join(
    ','
  )}&parent_type=course&parent_id=${courseId}`
  return VIDEOS.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchVideosForLearningPath = async videoIdsAndPathId => {
  const { videoIds, pathId } = videoIdsAndPathId
  const route = `videos?page_size=none&ids=${shortenUUIDs(videoIds).join(
    ','
  )}&parent_type=learning-path&parent_id=${pathId}`
  return VIDEOS.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchVideo = async payload => {
  const { id, parentId, parentType } = payload
  const route = isParentPresent({ parentId, parentType })
    ? `videos/${id}?parent_type=${parentType}&parent_id=${parentId}`
    : `videos/${id}`
  return VIDEOS.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const postVideoStatus = async videoIdAndStatus => {
  const { id, status, parentId, parentType } = videoIdAndStatus
  const route = isParentPresent({ parentId, parentType })
    ? `videos/${id}/status?parent_type=${parentType}&parent_id=${parentId}`
    : `videos/${id}/status?`
  return VIDEOS.post(route, { status })
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchQuizzes = async payload => {
  const { quizIds, parentId, parentType } = payload
  const route = isParentPresent({ parentId, parentType })
    ? `quizzes?page_size=none&ids=${shortenUUIDs(quizIds).join(
        ','
      )}&parent_type=${parentType}&parent_id=${parentId}`
    : `quizzes?page_size=none&ids=${shortenUUIDs(quizIds).join(',')}`
  return QUIZ.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchQuizzesForLearningPath = async quizIdsAndPathId => {
  const { quizIds, pathId } = quizIdsAndPathId
  const route = `quizzes?page_size=none&ids=${shortenUUIDs(quizIds).join(
    ','
  )}&parent_type=learning-path&parent_id=${pathId}`
  return QUIZ.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchExercises = async payload => {
  const { exerciseIds, parentType, parentId } = payload
  const route = isParentPresent({ parentId, parentType })
    ? `exercises?page_size=none&ids=${shortenUUIDs(exerciseIds).join(
        ','
      )}&parent_type=${parentType}&parent_id=${parentId}`
    : `exercises?page_size=none&ids=${shortenUUIDs(exerciseIds).join(',')}`
  return EXERCISES.get(route)
    .then(response => {
      // service doesn't return objects in the same order than IDs array,
      // ordering here avoid us to transfer 2 arrays through components and refactor code.
      const idsresponse = response.data.reduce((acc, e) => {
        acc[e.id] = e
        return acc
      }, {})
      const orderedResponse = []
      for (let index = 0; index < exerciseIds.length; index++) {
        const id = exerciseIds[index]
        orderedResponse.push(idsresponse[id])
      }
      return Promise.resolve(orderedResponse)
    })
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchExercisesForLearningPath = async exerciseIdsAndPathId => {
  const { exerciseIds, pathId } = exerciseIdsAndPathId
  const route = `exercises?page_size=none&ids=${shortenUUIDs(exerciseIds).join(
    ','
  )}&parent_type=learning-path&parent_id=${pathId}`
  return EXERCISES.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchLabs = async payload => {
  const { labIds, parentType, parentId } = payload
  const route = isParentPresent({ parentId, parentType })
    ? `labs?page_size=none&ids=${shortenUUIDs(labIds).join(
        ','
      )}&parent_type=${parentType}&parent_id=${parentId}`
    : `labs?page_size=none&ids=${shortenUUIDs(labIds).join(',')}`
  return CONTENT.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchLabsForLearningPath = async labIdsAndPathId => {
  const { labIds, pathId } = labIdsAndPathId
  const route = `labs?page_size=none&ids=${shortenUUIDs(labIds).join(
    ','
  )}&parent_type=learning-path&parent_id=${pathId}`
  return CONTENT.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const postLabStatus = async labIdAndStatus => {
  const { id, status, parentId, parentType } = labIdAndStatus
  const route = isParentPresent({ parentId, parentType })
    ? `labs/${id}/status?parent_type=${parentType}&parent_id=${parentId}`
    : `labs/${id}/status`
  return CONTENT.post(route, { status })
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const postIFrameStatus = async iframeIdAndStatus => {
  const { id, status, parentId, parentType } = iframeIdAndStatus
  const route = isParentPresent({ parentId, parentType })
    ? `iframes/${id}/status?parent_type=${parentType}&parent_id=${parentId}`
    : `iframes/${id}/status`
  return CONTENT.post(route, {
    status,
  })
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchIFramesForLearningPath = async iframeIdsAndPathId => {
  const { iframeIds, pathId } = iframeIdsAndPathId
  const route = `iframes?page_size=none&ids=${shortenUUIDs(iframeIds).join(
    ','
  )}&parent_type=learning-path&parent_id=${pathId}`
  return CONTENT.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchBookmarks = async () => {
  const route = `bookmarks?page_size=none`
  return VIDEOS.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const postBookmark = async bookmark => {
  const { videoId: video, time, notes, courseId: course_id } = bookmark
  const route = `bookmarks`
  return VIDEOS.post(route, { video, time, notes, metadata: { course_id } })
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const deleteBookmark = async id => {
  const route = `bookmarks/${id}`
  return VIDEOS.delete(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchCoursesByIds = async (courseIds, page_size = 'none', page = 1) => {
  const route = `courses?page_size=${page_size}&page=${page}&ids=${shortenUUIDs(
    courseIds
  ).join(',')}`
  return CONTENT.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

// Update legacy 'has-watched' for legacy metrics
/* eslint-disable */
const postLegacyStatus = userAndVideoId => {
  const { userId, videoId: video_id } = userAndVideoId
  const route = `users/${userId}/video`
  return API.put(route, { video_id })
    .then(response => {
      return
    })
    .catch(error => {
      return
    })
}
/* eslint-enable */

const postMixpanelEvent = async payload => {
  return MIXPANEL.post('track', payload)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const fetchContentIssues = async () => {
  const route = `content-issues/types`
  return CONTENT.get(route)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

const postContentIssue = async issue => {
  const route = `content-issues`
  return CONTENT.post(route, issue)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(djangoErrorHandler(error)))
}

export {
  fetchJWVideo,
  postVideoStatus,
  fetchVideos,
  fetchVideosForBootcamp,
  fetchVideosForCourse,
  fetchVideosForLearningPath,
  fetchVideo,
  fetchQuizzes,
  fetchExercises,
  fetchLabs,
  postLabStatus,
  fetchBookmarks,
  postBookmark,
  deleteBookmark,
  fetchCoursesByIds,
  postLegacyStatus,
  postIFrameStatus,
  postMixpanelEvent,
  fetchQuizzesForLearningPath,
  fetchExercisesForLearningPath,
  fetchLabsForLearningPath,
  fetchIFramesForLearningPath,
  fetchRecentActivity,
  fetchContentIssues,
  postContentIssue,
}
